import React, { useState, useEffect } from 'react'
import { Calendar, Clock, Play, ChevronDown, ChevronUp } from 'lucide-react'
import { formatDuration, formatGuestName, formatSummary, formatTimestamp } from '../utils/textUtils'
import { MetadataService } from '../services/MetadataService'
import { usePodcastContext } from '../contexts/PodcastContext'
import { DataService } from '../services/DataService'
import EpisodeContentFloodcast from './EpisodeContentFloodcast'
import EpisodeContentPodchiasse from './EpisodeContentPodchiasse'
import EpisodeSection from './EpisodeSection'

const EpisodeCard = ({ episode, viewMode = 'minimal' }) => {
  const { setCurrentTrack } = usePodcastContext()
  const [expanded, setExpanded] = useState(viewMode === 'detailed')

  // Réagir aux changements de viewMode
  useEffect(() => {
    if (viewMode === 'detailed') {
      setExpanded(true)
    } else if (viewMode === 'minimal') {
      setExpanded(false)
    }
  }, [viewMode])

  const handlePlayEpisode = (timestamp = null) => {
    const spotifyUrl = DataService.getSpotifyUrl(episode.id, timestamp)
    
    const trackData = {
      episodeId: episode.id,
      title: episode.title,
      timestamp,
      spotifyUrl,
      isPlaying: false
    }
    
    setCurrentTrack(trackData)
  }

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'Date inconnue'
    }
  }

  const getPublicationDate = () => {
    const metadataDate = MetadataService.getPublicationDate(episode.id)
    if (metadataDate) {
      return metadataDate
    }
    return episode.date
  }

  const renderBestContent = () => {
    if (!episode.bestScoredContent || episode.bestScoredContent.length === 0) {
      return null
    }

    return (
      <div className="mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {episode.bestScoredContent.map((item, index) => (
            <div key={index} className="flex items-start gap-3 p-4 bg-black bg-opacity-20 rounded-lg border border-opacity-20 border-purple-300">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-text-secondary leading-relaxed mb-2">
                  {item.content}
                </p>
                <div className="flex items-center gap-2 text-xs">
                  <span className="badge badge-purple px-2 py-1 text-xs">
                    {item.sectionDisplayName}
                  </span>
                  {item.timestamp && (
                    <button
                      onClick={() => handlePlayEpisode(item.timestamp)}
                      className="badge badge-green px-2 py-1 cursor-pointer transition-colors text-xs"
                    >
                      {formatTimestamp(item.timestamp)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Bouton d'expansion - seulement en mode minimal */}
        {viewMode === 'minimal' && !expanded && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setExpanded(true)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <ChevronDown size={16} />
              <span>Voir plus</span>
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <article className="card transition-all duration-300 group p-8">
      {/* En-tête avec titre et bouton play */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 pr-8">
          <h3 className="text-xl font-semibold text-text-primary mb-4 leading-tight">
            {episode.title}
          </h3>
          
          <div className="flex flex-wrap items-center text-sm text-text-muted gap-6">
            {getPublicationDate() && (
              <div className="flex items-center gap-2">
                <Calendar size={16} />
                <span>{formatDate(getPublicationDate())}</span>
              </div>
            )}
            
            {episode.duration && (
              <div className="flex items-center gap-2">
                <Clock size={16} />
                <span>{formatDuration(episode.duration)}</span>
              </div>
            )}
            
            {!expanded && (
              (episode.podcast === 'podchiasse' ? episode.participants : episode.guests)?.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  {(episode.podcast === 'podchiasse' ? episode.participants : episode.guests).map((person, index) => (
                    <span
                      key={index}
                      className="badge badge-blue px-2 py-1 text-xs"
                    >
                      {formatGuestName(person)}
                    </span>
                  ))}
                </div>
              )
            )}
          </div>
        </div>

        <button
          onClick={() => handlePlayEpisode()}
          className="btn btn-primary flex-shrink-0"
        >
          <Play size={16} className="shrink-0" />
          <span className="hidden sm:inline ml-2">Écouter</span>
        </button>
      </div>

      {/* Résumé - seulement en mode minimal */}
      {!expanded && episode.summary && (
        <div className="mb-8">
          <p className="text-text-secondary text-base leading-relaxed line-clamp-3 px-1">
            {formatSummary(episode.summary)}
          </p>
        </div>
      )}


      {/* Meilleurs contenus avec scores - seulement en mode minimal et pas étendu */}
      {viewMode === 'minimal' && !expanded && renderBestContent()}
      
      {/* Contenu étendu */}
      {(expanded || viewMode === 'detailed') && (
        <div className="mt-2 border-t border-opacity-20 border-purple-300">
          {/* Invités/Participants en version étendue */}
          {episode.podcast === 'podchiasse' ? (
            <EpisodeContentPodchiasse episode={episode} compact={false} />
          ) : (
            <EpisodeContentFloodcast episode={episode} compact={false} />
          )}
          
          {/* Sections détaillées */}
          {episode.podcast === 'podchiasse' ? (
            <>
              <EpisodeSection
                title="Histoires d'agacement"
                items={episode.annoyance_stories}
                type="annoyance"
                onPlayEpisode={handlePlayEpisode}
                colorClass="red"
              />
              
              <EpisodeSection
                title="Moments farssissages"
                items={episode.farfie_moments}
                type="farfie"
                onPlayEpisode={handlePlayEpisode}
                colorClass="green"
              />
              
              <EpisodeSection
                title="Moments notables"
                items={episode.notable_moments}
                type="moments"
                onPlayEpisode={handlePlayEpisode}
                colorClass="yellow"
              />
            </>
          ) : (
            <>
              <EpisodeSection
                title="Actualités"
                items={episode.introNews}
                type="news"
                onPlayEpisode={handlePlayEpisode}
                colorClass="blue"
              />
              
              <EpisodeSection
                title="Jeux"
                items={episode.games}
                type="games"
                onPlayEpisode={handlePlayEpisode}
                colorClass="green"
              />
              
              <EpisodeSection
                title="Moments notables"
                items={episode.notable_moments}
                type="moments"
                onPlayEpisode={handlePlayEpisode}
                colorClass="yellow"
              />
              
              <EpisodeSection
                title="Recommandations culturelles"
                items={episode.cultureRecommendations}
                type="culture"
                onPlayEpisode={handlePlayEpisode}
                colorClass="orange"
              />
            </>
          )}
          
          {/* Bouton de fermeture en bas - seulement en mode minimal */}
          {viewMode === 'minimal' && (
            <div className="flex justify-center">
              <button
                onClick={() => setExpanded(false)}
                className="btn btn-secondary flex items-center gap-2"
              >
                <ChevronUp size={16} />
                <span>Voir moins</span>
              </button>
            </div>
          )}
        </div>
      )}
    </article>
  )
}

export default React.memo(EpisodeCard, (prevProps, nextProps) => {
  // Ne re-render que si l'épisode ou le viewMode changent
  return prevProps.episode.id === nextProps.episode.id && 
         prevProps.viewMode === nextProps.viewMode
})